<template>
  <button
    v-if="cptdClientId && Object.keys(msalInstance || {}).length"
    class="MicrosoftSignInButton"
    @click="msalSignIn"
  >
    <BaseIconImpl
      :url="
        defineAsyncComponent(
          () =>
            import(
              '../../../assets/images/logos/providers/microsoft.svg?component'
            ),
        )
      "
      name="microsoft-logo"
    />
    <span>Continuar com Microsoft</span>
  </button>
</template>

<script>
import * as msal from '@azure/msal-browser'
import { mapActions } from 'vuex'
import { Provider } from '@/data/oauth/oauth'
import { parseJwt } from '@/helpers/globalFunctions'
import BaseIconImpl from '@/components/commomComponents/BaseIconImpl.vue'
import { defineAsyncComponent } from 'vue'
export default {
  name: 'MicrosoftSignInButton',
  components: {
    BaseIconImpl,
  },
  props: {
    context: {
      type: String,
      default: 'login',
    },
    redirect: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['login', 'registered', 'linked'],
  data() {
    return {
      busy: true,
      msalInstance: null,
      isAuthenticated: false,
    }
  },
  computed: {
    cptdClientId() {
      return import.meta.env.VITE_OAUTH_MICROSOFT_CLIENT_ID
    },
  },
  async created() {
    this.msalInstance = new msal.PublicClientApplication({
      auth: {
        clientId: this.cptdClientId,
      },
      cache: {
        cacheLocation: 'sessionStorage',
        storeAuthStateInCookie: false,
      },
    })
    await this.msalInstance.initialize()
  },
  mounted() {
    this.busy = false
    this.actEmitGaEvent({
      eventCategory: 'microsoft-signin-button',
      eventAction: `button-displayed`,
      eventLabel: `button-displayed`,
    })
  },
  methods: {
    defineAsyncComponent,
    ...mapActions({
      actOAuthLogin: 'oauth/actOAuthLogin',
      actOAuthLink: 'oauth/actOAuthLink',
      createFeedback: 'utils/feedback/createFeedback',
      actEmitGaEvent: 'services/ga/actEmitGaEvent',
    }),
    async msalSignIn() {
      try {
        const loginResponse = await this.msalInstance.loginPopup()
        this.isAuthenticated = !!loginResponse.account
        if (this.isAuthenticated) {
          this.handleSubmit(loginResponse)
        }
      } catch (ex) {
        this.isAuthenticated = false
        this.createFeedback({
          text: 'Algo deu errado! Por favor tente novamente.',
        })
      }
    },
    handleSubmit(response) {
      switch (this.context) {
        case 'login':
          this.login(response?.idToken)
          break
        case 'register':
          this.register(response?.idToken)
          break
        case 'link':
          this.link(response?.idToken)
          break
      }
    },
    async login(accessToken) {
      this.actEmitGaEvent({
        eventCategory: 'microsoft-signin-button',
        eventAction: `login-initiated`,
        eventLabel: `login-initiated`,
      })
      const response = await this.actOAuthLogin({
        provider: Provider.MICROSOFT,
        accessToken,
      })
      if (response?.status === 200) {
        this.actEmitGaEvent({
          eventCategory: 'microsoft-signin-button',
          eventAction: `login-finished`,
          eventLabel: `login-finished`,
        })
        this.$emit('login')
      } else if (response?.status === 401) {
        this.actEmitGaEvent({
          eventCategory: 'microsoft-signin-button',
          eventAction: `login-401`,
          eventLabel: `login-401`,
        })
        this.createFeedback({
          text: 'Não encontramos uma conta associada à este email. Cadastre-se ou faça o login utilizando a opção de cpf/email para continuar.',
        })
      }
    },
    register(accessToken) {
      const { name, email } = parseJwt(accessToken)
      const firstName = (name ?? '').split(' ')[0]
      this.actEmitGaEvent({
        eventCategory: 'microsoft-signin-button',
        eventAction: `register-initiated`,
        eventLabel: `register-initiated`,
      })
      if (this.redirect) {
        sessionStorage.setItem(
          'jb.state.oauth.provider-data',
          JSON.stringify({
            accessToken,
            provider: Provider.MICROSOFT,
            name: firstName,
            email,
          }),
        )
        this.$router.push({
          path: '/criar-conta',
        })
        return
      }

      this.$emit('registered', {
        accessToken,
        provider: Provider.MICROSOFT,
        name: firstName,
        email,
      })
    },
    async link(accessToken) {
      this.actEmitGaEvent({
        eventCategory: 'microsoft-signin-button',
        eventAction: `link-initiated`,
        eventLabel: `link-initiated`,
      })
      const response = await this.actOAuthLink({
        provider: Provider.MICROSOFT,
        accessToken,
      })

      if (response?.status === 200 || response?.status === 401) {
        this.actEmitGaEvent({
          eventCategory: 'microsoft-signin-button',
          eventAction: `link-finished`,
          eventLabel: `link-finished`,
        })
        this.$emit('linked')
        this.createFeedback({
          text: 'Conta atualizada com sucesso. No seu próximo login utilize o botão "Continuar com Microsoft".',
        })
        return
      }

      this.createFeedback({
        text: 'Algo deu errado! Por favor tente novamente.',
      })
      this.actEmitGaEvent({
        eventCategory: 'microsoft-signin-button',
        eventAction: `link-error`,
        eventLabel: `link-error`,
      })
    },
    click() {
      this.actEmitGaEvent({
        eventCategory: 'microsoft-signin-button',
        eventAction: 'button-clicked',
        eventLabel: this.context,
      })
    },
  },
}
</script>

<style lang="scss">
.MicrosoftSignInButton {
  max-width: 300px;
  width: 300px;
  cursor: pointer;
  transition: all 0.1s ease;
  white-space: nowrap;
  border-radius: 10000px;
  overflow: hidden;
  display: flex;
  align-items: center;
  border: solid 1px $color-neutral-dark;
  background-color: $color-white-high;
  height: 40px;
  padding: 0 12px;

  & > .BaseIcon {
    width: 18px !important;
    max-width: 18px;
  }

  & > span {
    @include subtitle-2;
    color: $color-black-medium;
    margin-left: 12px;
    text-align: center;
    width: calc(100% - 18px);
  }
}
</style>
